import type { NavigatePort } from '@heidi/runtime/shell';
import { useLatestRef } from '@heidi/utils';
import { useEffect } from 'react';

export interface EhrLaunchSessionRedirectInput {
  /** Session in the URL, empty on a bare `/scribe/sessions`. */
  sessionId: string;
  /** Session the launch resolved; only ever set in a launch, empty otherwise. */
  launchSessionId: string;
  /** Where the redirect lands — the app binds this to its router. */
  onNavigate: NavigatePort;
}

/**
 * An EHR launch is scoped to the session it resolved, and the list is withheld
 * there, so a bare `/scribe/sessions` has nothing left to render. Put the user
 * back on that session rather than on an empty workspace.
 *
 * Keyed on the values, not on `onNavigate`, which is read through a latched ref
 * so a fresh callback identity each render can't retrigger the redirect.
 */
export const useEhrLaunchSessionRedirect = ({
  sessionId,
  launchSessionId,
  onNavigate,
}: EhrLaunchSessionRedirectInput) => {
  const onNavigateRef = useLatestRef(onNavigate);

  useEffect(() => {
    if (sessionId || !launchSessionId) return;
    void onNavigateRef.current(`/scribe/sessions/${launchSessionId}`);
  }, [sessionId, launchSessionId, onNavigateRef]);
};
